import {Anchor, Group, Loader, Paper, Stack, Text, Title} from '@mantine/core'
import {Link} from '@tanstack/react-router'

import {formatBusinessTimestamp} from 'src/api/business-time'
import {JobStatusBadge} from 'src/jobs/job-status-badge'
import type {JobLifecycleStatus} from 'src/jobs/types'
import {useProjects} from 'src/projects/use-projects'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

import {CustomerOpenWorkSection} from '../work/customer-open-work-section'
import {CustomerContactsSection} from './customer-contacts-section'
import {CustomerDetailsPageTitle} from './customer-details-page-title'
import {CustomerDocumentsSection} from './customer-documents-section'
import {CustomerPaymentTermsSection} from './customer-payment-terms-section'
import {CustomerSavedLocationsSection} from './customer-saved-locations-section'
import {useCustomer} from './use-customers'

type CustomerDetailsPageProps = {
    customerId: string
}

function formatCreatedAt(createdAt: string) {
    return formatBusinessTimestamp(createdAt, 'MMM d, yyyy')
}

function isNotFoundError(error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to load customer'
    return message.toLowerCase().includes('not found')
}

export function CustomerDetailsPage({customerId}: CustomerDetailsPageProps) {
    const {data: customer, isLoading, isError, error} = useCustomer(customerId)
    const {data: projects = [], isLoading: projectsLoading} = useProjects(
        {customerId, sortBy: 'created_at', sortDir: 'desc'},
        {enabled: Boolean(customer)},
    )

    if (isLoading) {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        )
    }

    if (isError && isNotFoundError(error)) {
        return (
            <Stack gap="md">
                <Title order={1}>Customer not found</Title>
                <Text c="dimmed">This customer may have been removed or the link is invalid.</Text>
                <DetailPageBackLink to="/customers" listLabel="customers" />
            </Stack>
        )
    }

    if (isError || !customer) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load customer'
        return (
            <Stack gap="md">
                <Title order={1}>Customer details</Title>
                <Text c="red">{errorMessage}</Text>
                <DetailPageBackLink to="/customers" listLabel="customers" />
            </Stack>
        )
    }

    return (
        <Stack gap="lg">
            <CustomerDetailsPageTitle customer={customer} />

            <CustomerOpenWorkSection customerId={customerId} />

            <CustomerPaymentTermsSection customer={customer} />

            <CustomerContactsSection customerId={customerId} contacts={customer.contacts} />

            <CustomerSavedLocationsSection customerId={customerId} />

            <CustomerDocumentsSection customerId={customerId} />

            <Paper withBorder p="md" radius="md">
                <Stack gap="md">
                    <Title order={3}>Orders</Title>
                    {projectsLoading ? (
                        <Group justify="center" py="md">
                            <Loader size="sm" />
                        </Group>
                    ) : projects.length === 0 ? (
                        <Text size="sm" c="dimmed">
                            No active orders for this customer.
                        </Text>
                    ) : (
                        <Stack gap="xs">
                            {projects.map((project) => (
                                <Group key={project.id} justify="space-between" wrap="nowrap">
                                    <Stack gap={0}>
                                        <Anchor
                                            component={Link}
                                            to="/projects/$projectId"
                                            params={{projectId: project.id}}
                                            size="sm"
                                            fw={500}
                                        >
                                            {project.name}
                                        </Anchor>
                                        <Text size="xs" c="dimmed">
                                            Created {formatCreatedAt(project.createdAt)}
                                        </Text>
                                    </Stack>
                                    <JobStatusBadge
                                        status={project.status satisfies JobLifecycleStatus}
                                    />
                                </Group>
                            ))}
                        </Stack>
                    )}
                </Stack>
            </Paper>
        </Stack>
    )
}
